import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { Box, Button } from '@mui/material';
import Divider from '@mui/material/Divider';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ArtworkLikeStatProvider from 'commons/ArtworkLikeStatProvider';
import Breadcrumbs from 'commons/Breadcrumbs';
import ShareSocialPopup from 'commons/ShareSocialPopup';
import Text from 'commons/Text';
import Colors from 'configs/Colors';
import { CERTIFICATE, SHOW, getLabelForOption } from 'configs/Constant';
import Routes from 'utils/Route';
import { isValidEmailAddress, isValidURL } from 'utils/Validation';
import { formatCurrency, getTextEdition } from 'utils/utils';

import AttributeField from '../ArtworkInfo/AttributeField';
import ArtworkLikeUI from '../ArtworkLikeUI';
import CertificateDialog from '../CertificateDialog';
import useStyles from './styles';

const ArtworkDetailLeft = ({ breadCrumbs, artwork }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpenSharePopup, setIsOpenSharePopup] = useState(false);

  const language = useSelector((state) => state?.language?.currentLanguage);

  const {
    title,
    artist,
    status,
    year_created,
    period_created,
    size,
    certificate,
    medium,
    total_edition,
    price,
    currency,
    contact_information,
    artist_artwork,
    has_certificate,
  } = artwork || {};

  const priceFormat = formatCurrency(price);

  const sizeData =
    size?.height &&
    size?.width &&
    `${size?.height} x ${size?.width} ${size?.depth ? ` x ${size?.depth}` : ''}cm `;

  const isHidePrice = artwork?.is_hide_price;
  const isShowAskPrice = artwork?.is_ask_price_visible;
  const isOpen = searchParams.get(CERTIFICATE) === SHOW;

  const artistName = artist?.name || artist_artwork?.artist_name || 'N/A';
  const isPlatformArtist = !!artist;
  const canNavigateToArtist = isPlatformArtist && certificate;

  const handleArtistNameClick = () => {
    if (canNavigateToArtist) {
      navigate(Routes.GalleryDetail(artist.uuid));
    }
  };

  const handleContactClick = () => {
    if (contact_information) {
      if (isValidURL(contact_information)) {
        window.open(contact_information, '_blank');
      } else if (isValidEmailAddress(contact_information)) {
        window.location.href = `mailto:${contact_information}`;
      }
    }
  };

  const handleShowDialog = () => {
    searchParams.set(CERTIFICATE, SHOW);
    setSearchParams(searchParams, { replace: true });
  };

  const handleCloseDialog = () => {
    searchParams.delete(CERTIFICATE);
    setSearchParams(searchParams, { replace: true });
  };

  const handleOpenSocialPopup = () => {
    setIsOpenSharePopup(true);
  };

  const handleCloseSocialPopup = () => {
    setIsOpenSharePopup(false);
  };

  return (
    <Box className={classes.wrapHeader}>
      <Breadcrumbs
        items={breadCrumbs}
        breadcrumbsStyle={classes.breadcrumbsStyle}
      />
      <Text
        fontWeightBold
        color={Colors.Grey10}
        type="2xl"
        variant="h1"
        className={classes.textTitle}
      >
        {title}
      </Text>

      <Box className={classes.wrapAttributeField}>
        <AttributeField
          attributeName={`${t('artist')}:`}
          attributeValue={artistName}
          attributeValueStyle={canNavigateToArtist ? classes.link : classes.linkNotUnderLine}
          onValueClick={handleArtistNameClick}
        />

        <AttributeField
          attributeName={`${t('year_of_creation')}:`}
          attributeValue={year_created || period_created}
        />

        <AttributeField
          attributeName={`${t('medium_material')}:`}
          attributeValue={getLabelForOption(medium, language)}
        />

        <AttributeField
          attributeName={`${t('measurement_w_h')}:`}
          attributeValue={sizeData}
        />

        {certificate?.edition_number && (
          <AttributeField
            attributeName={t('edition')}
            attributeValue={getTextEdition(certificate?.edition_number, total_edition)}
          />
        )}

        <AttributeField
          attributeName={`${t('certificate_of_authenticity')}:`}
          attributeValue={artwork?.certificate && t('issued_on_gladius_art')}
          attributeValueStyle={classes.link}
          onValueClick={handleShowDialog}
        />

        <AttributeField
          attributeName={`${t('certificate_of_authenticity')}:`}
          attributeValue={!artwork?.certificate && has_certificate && t('issued_on_gladius_art')}
          attributeValueStyle={has_certificate && classes.linkNotUnderLine}
        />

        <AttributeField
          attributeName={`${t('status')}:`}
          attributeValue={t(status?.key)}
          attributeValueStyle={classes.attributeField}
        />
      </Box>

      <Box className={classes.wrapBoxPriceAndIcon}>
        <Divider />
        {priceFormat && priceFormat !== 0 && !isHidePrice && (
          <div className={classes.wrapPrice}>
            <Text>{t('price')}:</Text>
            <Text
              className={classes.price}
              fontWeightBold
            >
              {`${priceFormat} ${currency}`}
            </Text>
          </div>
        )}
        {isShowAskPrice && (
          <div className={classes.wrapPrice}>
            <Text>{`${t('price')}:`}</Text>
            <Text
              className={classes.askPrice}
              onClick={handleContactClick}
            >
              {`${t('ask_for_price')}`}
            </Text>
          </div>
        )}
        <div className={classes.wrapIcon}>
          <ArtworkLikeStatProvider artwork={artwork}>
            <ArtworkLikeUI artworkId={artwork?.id} />
          </ArtworkLikeStatProvider>

          <Button
            variant="outlined"
            fullWidth
            className={classes.button1}
            onClick={handleOpenSocialPopup}
          >
            <ShareOutlinedIcon />
          </Button>
          {isShowAskPrice && (
            <Button
              variant="contained"
              fullWidth
              className={classes.button2}
              onClick={handleContactClick}
            >
              <EmailOutlinedIcon />
              <Text className={classes.textInquire}>{t('inquire')}</Text>
            </Button>
          )}
        </div>
      </Box>

      {artwork?.certificate && (
        <CertificateDialog
          open={isOpen ? isOpen : false}
          onClose={handleCloseDialog}
          artwork={artwork}
          certificate={artwork?.certificate}
        />
      )}

      <ShareSocialPopup
        open={isOpenSharePopup}
        onClose={handleCloseSocialPopup}
        fullWidth
        maxWidth="sm"
      />
    </Box>
  );
};

export default ArtworkDetailLeft;
